(() => {
  if (!customElements.get('mission-timeline')) {
    class MissionTimeline extends HTMLElement {
      constructor() {
        super();

        this.activeIndex = 0;
        this.resizeObserver = null;
        this.scrollRaf = null;
        this.resizeRaf = null;
        this.lastScrollTop = null;
        this.listHeightValue = null;
        this.listPaddingBottom = null;
      }

      connectedCallback() {
        this.setProperties();

        if (!this.listItems.length && !this.accordionItems.length) {
          return;
        }

        this.setActiveIndex(this.getInitialIndex(), {
          force: true,
          updateAccordion: this.hasActiveAccordionItem(),
        });
        this.syncLayout();
        this.setEventListeners();
      }

      disconnectedCallback() {
        this.removeEventListeners();
        cancelAnimationFrame(this.scrollRaf);
        cancelAnimationFrame(this.resizeRaf);
      }

      setProperties() {
        this.mediaItems = Array.from(this.querySelectorAll('[data-mission-timeline-media-item]'));
        this.listItems = Array.from(this.querySelectorAll('[data-mission-timeline-item]'));
        this.accordionItems = Array.from(this.querySelectorAll('[data-mission-timeline-accordion-item]'));
        this.listScroll = this.querySelector('[data-mission-timeline-list-scroll]');
        this.list = this.querySelector('[data-mission-timeline-list]');
        this.media = this.querySelector('[data-mission-timeline-media]');
        this.desktopContent = this.querySelector('[data-mission-timeline-content]');
        this.desktopHeader = this.querySelector('[data-mission-timeline-header]');
        this.scrollbar = this.querySelector('[data-mission-timeline-scrollbar]');
        this.scrollbarThumb = this.querySelector('[data-mission-timeline-scrollbar-thumb]');
        this.desktopQuery = window.matchMedia(window.themeVariables?.mediaQueries?.md ?? '(min-width: 1000px)');
        this.useSharedImage = this.dataset.useSharedImage === 'true';

        this.handleClick = this.onClick.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleListScroll = this.onListScroll.bind(this);
        this.handleBlockSelect = this.onBlockSelect.bind(this);
        this.handleDesktopChange = this.onDesktopChange.bind(this);
      }

      setEventListeners() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.listScroll?.addEventListener('scroll', this.handleListScroll, { passive: true });
        this.desktopQuery.addEventListener('change', this.handleDesktopChange);
        this.setResizeObserver();

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', this.handleBlockSelect);
        }
      }

      removeEventListeners() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
        this.listScroll?.removeEventListener('scroll', this.handleListScroll);
        this.desktopQuery.removeEventListener('change', this.handleDesktopChange);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        if (window.Shopify?.designMode) {
          document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        }
      }

      setResizeObserver() {
        if (!window.ResizeObserver || !this.listScroll) {
          return;
        }

        this.resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this.resizeRaf);
          this.resizeRaf = requestAnimationFrame(() => {
            this.syncLayout();
          });
        });

        this.resizeObserver.observe(this.listScroll);

        if (this.list) {
          this.resizeObserver.observe(this.list);
        }

        if (this.media) {
          this.resizeObserver.observe(this.media);
        }
      }

      onDesktopChange() {
        this.syncLayout();
      }

      syncLayout() {
        this.updateListHeight();
        this.updateListPadding();
        this.updateScrollbar();
      }

      getItemCount() {
        return this.listItems.length;
      }

      getScrollProgressForIndex(index, count = this.getItemCount()) {
        if (count <= 1) {
          return 0;
        }

        return index / (count - 1);
      }

      getIndexFromScrollProgress(progress, count = this.getItemCount()) {
        if (count <= 1) {
          return 0;
        }

        return Math.min(Math.round(progress * (count - 1)), count - 1);
      }

      getInitialIndex() {
        const items = this.desktopQuery.matches ? this.listItems : this.accordionItems;
        const activeItem = items.find((item) => item.classList.contains('is-active'));
        const index = Number(activeItem?.dataset.index);

        return Number.isNaN(index) ? 0 : index;
      }

      hasActiveAccordionItem() {
        return this.accordionItems.some((item) => item.classList.contains('is-active'));
      }

      onClick(event) {
        const accordionToggle = event.target.closest('[data-mission-timeline-accordion-toggle]');
        const listItem = event.target.closest('[data-mission-timeline-item]');

        if (accordionToggle) {
          this.toggleAccordionItem(accordionToggle);
          return;
        }

        if (listItem && this.desktopQuery.matches) {
          this.activateListItem(listItem);
        }
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }

        const listItem = event.target.closest('[data-mission-timeline-item]');

        if (listItem && this.desktopQuery.matches) {
          event.preventDefault();
          this.activateListItem(listItem);
        }
      }

      toggleAccordionItem(trigger) {
        const item = trigger.closest('[data-mission-timeline-accordion-item]');
        const index = Number(item?.dataset.index);

        if (!item || Number.isNaN(index)) {
          return;
        }

        if (item.classList.contains('is-active')) {
          this.closeAccordionItem(item);
          return;
        }

        this.setActiveIndex(index, { force: true });
      }

      closeAccordionItem(item) {
        item.classList.remove('is-active');
        item.querySelectorAll('[data-mission-timeline-accordion-toggle]').forEach((toggle) => {
          toggle.setAttribute('aria-expanded', 'false');
        });
      }

      activateListItem(item) {
        const index = Number(item.dataset.index);

        if (Number.isNaN(index)) {
          return;
        }

        this.setActiveIndex(index, { force: true });
        this.scrollListItemIntoView(item);
      }

      onListScroll() {
        if (!this.desktopQuery.matches || !this.listScroll) {
          return;
        }

        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = requestAnimationFrame(() => {
          const scrollTop = this.listScroll.scrollTop;

          if (scrollTop !== this.lastScrollTop) {
            this.updateScrollbar();
            this.lastScrollTop = scrollTop;
          }

          const index = this.getActiveIndexFromScroll();

          if (index !== null) {
            this.setActiveIndex(index);
          }
        });
      }

      getActiveIndexFromScroll() {
        const count = this.getItemCount();

        if (!count) {
          return null;
        }

        const maxScrollTop = this.listScroll.scrollHeight - this.listScroll.clientHeight;

        if (maxScrollTop <= 0) {
          return 0;
        }

        const progress = Math.min(Math.max(this.listScroll.scrollTop / maxScrollTop, 0), 1);

        return this.getIndexFromScrollProgress(progress, count);
      }

      scrollListItemIntoView(item) {
        if (!this.listScroll || !item) {
          return;
        }

        const index = Number(item.dataset.index);
        const count = this.getItemCount();

        if (Number.isNaN(index) || !count) {
          return;
        }

        const maxScrollTop = Math.max(0, this.listScroll.scrollHeight - this.listScroll.clientHeight);
        const top = this.getScrollProgressForIndex(index, count) * maxScrollTop;

        this.listScroll.scrollTo({ top, behavior: 'auto' });
        this.lastScrollTop = top;
        this.updateScrollbar();
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) {
          return;
        }

        const blockId = event.detail.blockId;
        const listItem = this.listItems.find((item) => item.dataset.blockId === blockId);
        const accordionItem = this.accordionItems.find((item) => item.dataset.blockId === blockId);
        const activeItem = this.desktopQuery.matches ? listItem : accordionItem;
        const index = Number(activeItem?.dataset.index);

        if (Number.isNaN(index)) {
          return;
        }

        this.setActiveIndex(index, { force: true });

        if (listItem && this.desktopQuery.matches) {
          this.scrollListItemIntoView(listItem);
        }
      }

      setActiveIndex(index, options = {}) {
        const count = this.getItemCount();

        if (index < 0 || index >= count) {
          return;
        }

        if (!options.force && index === this.activeIndex) {
          return;
        }

        this.activeIndex = index;

        if (!this.useSharedImage && this.mediaItems.length) {
          this.mediaItems.forEach((item) => {
            const itemIndex = Number(item.dataset.index);
            item.classList.toggle('is-visible', itemIndex === index);
          });
        }

        this.listItems.forEach((item) => {
          const itemIndex = Number(item.dataset.index);
          const isActive = itemIndex === index;

          item.classList.toggle('is-active', isActive);

          if (isActive) {
            item.setAttribute('aria-current', 'true');
          } else {
            item.removeAttribute('aria-current');
          }
        });

        if (options.updateAccordion !== false) {
          this.accordionItems.forEach((item) => {
            const itemIndex = Number(item.dataset.index);
            const isActive = itemIndex === index;

            item.classList.toggle('is-active', isActive);
            item.querySelectorAll('[data-mission-timeline-accordion-toggle]').forEach((toggle) => {
              toggle.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            });
          });
        }
      }

      updateListHeight() {
        if (!this.listScroll || !this.media) {
          return;
        }

        if (!this.desktopQuery.matches) {
          if (this.listHeightValue !== null) {
            this.listHeightValue = null;
            this.style.removeProperty('--mission-timeline-list-height');
          }

          return;
        }

        const mediaHeight = Math.round(this.media.getBoundingClientRect().height);

        if (mediaHeight <= 0) {
          return;
        }

        const minimumListHeight = 200;
        const headerHeight = Math.round(this.desktopHeader?.getBoundingClientRect().height ?? 0);
        const contentStyles = this.desktopContent ? getComputedStyle(this.desktopContent) : null;
        const rowGap = contentStyles ? parseFloat(contentStyles.rowGap) || parseFloat(contentStyles.gap) || 0 : 0;
        const paddingTop = contentStyles ? parseFloat(contentStyles.paddingTop) || 0 : 0;
        const paddingBottom = contentStyles ? parseFloat(contentStyles.paddingBottom) || 0 : 0;
        const reservedHeight = headerHeight + (headerHeight > 0 ? rowGap : 0) + paddingTop + paddingBottom;
        const listHeight = Math.max(minimumListHeight, Math.round(mediaHeight - reservedHeight));
        const value = `${listHeight}px`;

        if (this.listHeightValue !== value) {
          this.listHeightValue = value;
          this.style.setProperty('--mission-timeline-list-height', value);
        }
      }

      updateListPadding() {
        if (!this.list || !this.listScroll || !this.listItems.length) {
          return;
        }

        if (!this.desktopQuery.matches) {
          if (this.listPaddingBottom !== null) {
            this.listPaddingBottom = null;
            this.list.style.removeProperty('padding-bottom');
          }

          return;
        }

        const containerHeight = this.listScroll.clientHeight;

        if (containerHeight <= 0) {
          return;
        }

        const lastItem = this.listItems[this.listItems.length - 1];
        const paddingBottom = `${Math.round(Math.max(0, (containerHeight - lastItem.offsetHeight) / 2))}px`;

        if (this.listPaddingBottom !== paddingBottom) {
          this.listPaddingBottom = paddingBottom;
          this.list.style.paddingBottom = paddingBottom;
        }
      }

      updateScrollbar() {
        if (!this.listScroll || !this.scrollbar || !this.scrollbarThumb) {
          return;
        }

        const maxScrollTop = this.listScroll.scrollHeight - this.listScroll.clientHeight;
        const trackHeight = this.scrollbar.clientHeight || this.listScroll.clientHeight;

        if (!this.desktopQuery.matches || maxScrollTop <= 0 || trackHeight <= 0) {
          this.scrollbar.classList.add('is-hidden');
          this.scrollbar.style.removeProperty('--mission-timeline-scrollbar-thumb-height');
          this.scrollbar.style.removeProperty('--mission-timeline-scrollbar-thumb-offset');
          return;
        }

        const thumbHeight = Math.max((this.listScroll.clientHeight / this.listScroll.scrollHeight) * trackHeight, 48);
        const maxOffset = Math.max(0, trackHeight - thumbHeight);
        const offset = (this.listScroll.scrollTop / maxScrollTop) * maxOffset;

        this.scrollbar.classList.remove('is-hidden');
        this.scrollbar.style.setProperty('--mission-timeline-scrollbar-thumb-height', `${thumbHeight}px`);
        this.scrollbar.style.setProperty('--mission-timeline-scrollbar-thumb-offset', `${offset}px`);
      }
    }

    customElements.define('mission-timeline', MissionTimeline);
  }
})();
